import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    DefaultButton,
    FontWeights,
    IconButton,
    IIconProps,
    Modal,
    Separator,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";
import {contentStyles, iconButtonStyles} from "./index";
import {useBranding} from "../../hooks/useBranding";
import Helper from "../../Helpers/Helper";
import {IGetApprovalActionResponse} from "../../services/Approval";
import StatusMessage from "../../share/StatusMessage";


type Properties = {
    showModal: boolean,
    action: IGetApprovalActionResponse | null
    onDismissModal: () => void
}

export function ApprovalEmailPreview({
                                         showModal,
                                         onDismissModal,
                                         action
                                     }: Properties) {

    const {t,} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const cancelIcon: IIconProps = {iconName: 'Cancel'};
    const [subject, setSubject] = useState("")
    const [body, setBody] = useState("")

    /**
     *
     */
    useEffect(() => {
        if (showModal) {
            setSubject(Helper.RemoveHTMLTags(action?.EmailSubject ?? ''))
            setBody(action?.EmailTemplate ?? '')
        }
    }, [showModal])


    /**
     * On Before dismiss
     */
    const onBeforeDismissEmailPreview = () => {
        onDismissModal()
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerRecordPreview}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.modalPreviewEmail', {ns: 'common', Name: `(${action?.Type})`})}>
                                {t('title.modalPreviewEmail', {ns: 'common', Name: `(${action?.Type})`})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={cancelIcon}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismissEmailPreview}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    <StatusMessage status={'info'} hasTimer={false}
                                                   truncated={false}
                                                   isMultiline={true}
                                                   dismiss={false}
                                                   setShowMessage={true}>
                                        <div>{t('text.previewEmailTags', {ns: 'approval'})}</div>
                                    </StatusMessage>

                                    <StackItem styles={{root: {paddingTop: 8}}}>
                                        <Text block
                                              variant={"large"}>
                                            {t('label.ctrlEmailTo', {ns: 'common'})}{":"}
                                        </Text>

                                        {action?.Type === 'Approve' &&
                                            <>
                                                {action?.EmailAddress !== '' &&
                                                    <>

                                                        <Text variant={"smallPlus"}
                                                              className={contentStyles(branding.theme).emailPreviewTags}>
                                                            {t('text.email', {ns: 'approval'})}
                                                        </Text>

                                                        <Text variant={"medium"} title={action?.EmailAddress}
                                                              styles={{root: {fontWeight: FontWeights.semibold}}}>
                                                            {action?.EmailAddress}
                                                        </Text>

                                                    </>
                                                }

                                                {action?.EmailAddressLookupRule !== '' &&

                                                    <>
                                                        <Text variant={"smallPlus"}
                                                              className={contentStyles(branding.theme).emailPreviewTags}>
                                                            {t('title.lookupRule', {ns: 'approval'})}
                                                        </Text>

                                                        <Text variant={"medium"} title={action?.EmailAddressLookupRule}
                                                              styles={{root: {fontWeight: FontWeights.semibold}}}>
                                                            {action?.EmailAddressLookupRule}
                                                        </Text>

                                                    </>
                                                }
                                            </>
                                        }

                                        {action?.AdditionalNotifys !== '' &&
                                            <>
                                                <Text variant={"smallPlus"}
                                                      className={contentStyles(branding.theme).emailPreviewTags}>
                                                    {t('text.otherEmail', {ns: 'approval'})}
                                                </Text>

                                                <Text variant={"smallPlus"}>
                                                    {action?.AdditionalNotifys}
                                                </Text>
                                            </>

                                        }
                                    </StackItem>

                                    <StackItem styles={{root: {paddingTop: 8}}}>
                                        <Text block
                                              variant={"large"}>{t('label.ctrlEmailSubject', {ns: 'common'})}{":"}</Text>
                                        <Text block
                                              variant={"medium"}>{subject}</Text>
                                    </StackItem>

                                    <StackItem styles={{root: {paddingTop: 8}}}>
                                        <Text block
                                              variant={"large"}>{t('label.ctrlEmailBody', {ns: 'common'})}{":"}</Text>
                                        <div style={{marginTop: 8}} dangerouslySetInnerHTML={{__html: body}}/>
                                        <Text styles={{root: {marginTop: "16"}}} block variant={"smallPlus"}>
                                            <p>_____</p>
                                            <p>{t('message.disclaimeCconfidentialData', {ns: 'common'})}</p>
                                        </Text>
                                    </StackItem>

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismissEmailPreview()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>
        </>
    )
}


